'use client';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ophelos/ui/components/tabs';
import { CreditCard, Landmark } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { DebtPageHeader } from '@/components/layout/DebtPageHeader';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useAuth } from '@/contexts/customer-auth-context';
import { useTranslation } from '@/hooks/useTranslation';
import { useTabLinkWhileOpen } from '@/hooks/useTabLinkWhileOpen';
import { Loader } from '@ophelos/ui/components/loader';
import { accountPaymentPath } from '@/lib/accountPayment';
import { soleCreditor } from '@/lib/creditor';
import { updatePaymentSessionStatusOnConnect } from '@/lib/connect-api';
import { applyCredit, creditApplied } from '@/lib/accountNet';
import { accountPaymentOffer } from '@/lib/accountPaymentOffer';
import { isUnpaidDebt } from '@/lib/debtFilters';
import { scheduledAccountPaymentAmount, scheduledAccountPaymentDate, scheduledAccountPaymentMethod } from '@/lib/paymentPlans';
import { type PaymentTab, resolveActivePaymentTab } from '@/lib/paymentTabs';
import { PaymentApiError, usePaymentApi } from '@/lib/payment-api';
import { planRefusalReason } from '@/lib/planRefusal';
import { ACCOUNT_SCOPE, paymentReturnUrl } from '@/lib/paymentReturnUrl';
import { planDeepLink } from '@/lib/redirect';
import { buildPlanSchedule, toApiSchedule, toScheduleDate } from '@/lib/payment-schedule';
import { AccountDebtBreakdown } from './_partials/_accountPaymentPartials/AccountDebtBreakdown';
import { AccountPartialPaymentTab } from './_partials/_accountPaymentPartials/AccountPartialPaymentTab';
import { AccountPayInFullTab } from './_partials/_accountPaymentPartials/AccountPayInFullTab';
import { PaymentOptionView } from './_partials/_singlePaymentPartials/PaymentOptionView';
import { AccountPaymentPlanTab } from './_partials/_accountPaymentPartials/AccountPaymentPlanTab';
import { AccountPaymentBlock } from './_partials/_singlePaymentPartials/AccountPaymentBlock';
import { PlanAuthoriseSheet } from './_partials/_singlePaymentPartials/PlanAuthoriseSheet';
import type { PaymentPlanData } from './_partials/_singlePaymentPartials/PaymentPlanTab';
import { ScheduledPaymentPanel } from './_partials/_singlePaymentPartials/ScheduledPaymentPanel';

export default function AccountPayment() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { config, isPreviewMode, isLoading: configLoading } = useConfiguration();
  const { t } = useTranslation();
  const { accountId, authStage, getAccessToken } = useAuth();
  const { debts: allDebts, isLoading, activeOffers, accountPaymentPlan, refreshDebts } = useDebtData();
  const { cancelPaymentPlan, createPaymentSession } = usePaymentApi();
  // The budget assessment hands the customer back here with the plan they were configuring.
  // Captured once, so the cleanup below can strip the params without losing them.
  const [paymentTab, setPaymentTab] = useState<'full' | 'partial' | 'plan'>(() => {
    const tab = searchParams.get('tab');
    return tab === 'partial' || tab === 'plan' ? tab : 'full';
  });
  // A tab link brings the Payment panel forward, so it lands even while the breakdown is open.
  const [mainTab, setMainTab] = useState<'payment' | 'breakdown'>('payment');
  useTabLinkWhileOpen(
    useCallback((tab: PaymentTab) => {
      setPaymentTab(tab);
      setMainTab('payment');
    }, []),
  );
  const [linkPrefill] = useState(() => ({
    frequency: searchParams.get('frequency'),
    start: searchParams.get('start'),
    count: searchParams.get('count'),
    offer: searchParams.get('offer'),
  }));
  const [isCancellingScheduled, setIsCancellingScheduled] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);
  // Card payment-plan review step at account scope.
  const [showPlanReview, setShowPlanReview] = useState(false);
  const [reviewPlanData, setReviewPlanData] = useState<PaymentPlanData | null>(null);
  // The verdict, held apart from the prefill above because the two have opposite lifetimes:
  // `reviewPlanData` must survive "Edit plan" to re-hydrate the form, while a verdict must not
  // survive a fresh submission that may have superseded it.
  const [decidedPlan, setDecidedPlan] = useState<PaymentPlanData | null>(null);
  // Why the customer was sent back from review, shown on the setup tab they land on.
  const [planReturnNotice, setPlanReturnNotice] = useState<string | null>(null);
  const [isProcessingReview, setIsProcessingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // One pass over the return-trip query params, because two effects each building `next`
  // from the same `searchParams` snapshot race: the later `setSearchParams` wins, the other's
  // deletions come back for a render, and the release request fires twice.
  //
  // `session_id` means a cancelled checkout: release the session so the account's
  // single-flight guard stops blocking, exactly as the case-level page does — without it an
  // explicit cancel is indistinguishable from closing the tab. The deep-link keys are
  // stripped once captured so a refresh or shared link does not re-apply stale values or
  // re-owe a confirmation (mirrors `Payment.tsx`).
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const deepLinkKeys = ['tab', 'frequency', 'start', 'count', 'offer'];
    const hasDeepLink = deepLinkKeys.some((k) => searchParams.has(k));
    if (!sessionId && !hasDeepLink) return;

    if (sessionId?.startsWith('psess_')) {
      updatePaymentSessionStatusOnConnect(sessionId, 'incomplete', authStage, getAccessToken).catch((err) => {
        // Non-fatal: the sweep is still the backstop, and the customer can keep paying.
        console.error('[AccountPayment] Failed to release cancelled session:', {
          session_id: sessionId,
          error: err,
        });
      });
    }

    const next = new URLSearchParams(searchParams);
    next.delete('session_id');
    for (const k of deepLinkKeys) next.delete(k);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, authStage, getAccessToken]);

  const dashboardPath = isPreviewMode ? '/dashboard?preview=true' : '/dashboard';
  // Above the early returns below — a hook after them changes hook order between renders.
  const offer = useMemo(
    () => accountPaymentOffer({ debts: allDebts, accountPaymentPlan, payment: config.payment }),
    [allDebts, accountPaymentPlan, config.payment],
  );
  const scheduledDate = scheduledAccountPaymentDate(accountPaymentPlan);
  const scheduledAmount = scheduledAccountPaymentAmount(accountPaymentPlan);
  const scheduledMethod = scheduledAccountPaymentMethod(accountPaymentPlan);

  const handleCancelScheduledPayment = async () => {
    const planId = accountPaymentPlan?.id;
    if (!planId) return;
    setIsCancellingScheduled(true);
    setScheduledError(null);
    try {
      await cancelPaymentPlan(planId);
      await refreshDebts();
      navigate('/dashboard?scheduled_cancelled=true');
    } catch (err) {
      setScheduledError(err instanceof PaymentApiError ? err.message : t('payment.cancelScheduledFailed'));
    } finally {
      setIsCancellingScheduled(false);
    }
  };

  // What an account payment would actually collect against: debts payable right now.
  const debts = useMemo(() => allDebts.filter(isUnpaidDebt), [allDebts]);

  const totalBalance = debts.reduce((sum, debt) => sum + debt.currentBalance, 0);
  // Only the credit attributed to the debts this surface collects against — an arranged
  // debt is outside that set, and its share of the credit belongs to it, not here.
  const payableBalance = applyCredit(totalBalance, creditApplied(debts));

  // The account plan can span creditors; name the single client, else a generic plural.
  const creditor = soleCreditor(debts);
  const reviewClientName = creditor ? creditor.originalCreditor : t('paymentPlan.review.yourCreditors');

  // Card plan advances to the review step; hold the configured plan so
  // "Edit plan" can return to setup with it prefilled.
  const handlePlanReview = (planData: PaymentPlanData) => {
    setPlanReturnNotice(null);
    setReviewPlanData(planData);
    setDecidedPlan(planData);
    setReviewError(null);
    setShowPlanReview(true);
  };

  const handleEditPlan = () => {
    setShowPlanReview(false);
    setReviewError(null);
  };

  // Create the account-level plan session — card only, from the review screen. The offer was
  // already submitted and accepted on the way in, so nothing is decided here.
  // Mirrors `Payment.tsx`.
  const executePlanFromReview = async () => {
    if (!accountId || !reviewPlanData) return;
    const { startDate, frequency, paymentCount, totalAmount: planTotal } = reviewPlanData;
    const currency = debts[0]?.currency ?? 'EUR';

    // Neither the discount pricing nor the balance is revalidated while review is open, so both
    // can go stale underneath it. `planRefusalReason` owns the decision so the two checks stay
    // independent — nesting one inside the other narrows the guard to the case where both apply.
    //
    // Say why, or pressing Authorise reads as a broken button: the setup screen just reappears
    // with a different total and no explanation. Carried as its own notice because
    // `handleEditPlan` clears the error slot on the way back.
    const refusal = planRefusalReason(reviewPlanData, payableBalance, activeOffers);
    if (refusal) {
      setPlanReturnNotice(t(refusal === 'offer-expired' ? 'countdown.offerExpired' : 'paymentPlan.review.balanceChanged'));
      handleEditPlan();
      return;
    }

    setIsProcessingReview(true);
    setReviewError(null);

    try {
      const baseUrl = window.location.origin;
      const schedule = toApiSchedule(buildPlanSchedule(planTotal, paymentCount, startDate, frequency));
      const response = await createPaymentSession({
        account_id: accountId,
        currency,
        payment_method: 'card',
        payment_type: 'payment_plan',
        description: `Payment plan covering ${debts.length} debts (${paymentCount} ${frequency} payments)`,
        success_url: paymentReturnUrl({ flow: 'plan', scope: ACCOUNT_SCOPE }),
        cancel_url: `${baseUrl}${planDeepLink({ basePath: '/account-payment', frequency, startDate, count: paymentCount })}`,
        payment_plan_schedule: schedule,
        payment_plan_frequency: frequency,
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Invalid payment response: no checkout URL');
      }
    } catch (err) {
      setIsProcessingReview(false);
      setReviewError(err instanceof PaymentApiError ? (err.status === 409 ? t('payment.planConflict') : err.message) : t('paymentPlan.setupFailed'));
    }
  };

  const tabs = offer.surface === 'open' ? offer.tabs : null;
  const blockedBy = offer.surface === 'blocked' ? offer.blockedBy : null;

  // `?tab=` can name a tab this operator does not offer, and a tab with neither trigger nor
  // body rendered leaves the payment area empty. Shares `resolveActivePaymentTab` with
  // `Payment.tsx` rather than restating the rule — the two had already drifted once, which is
  // why the rule was extracted. This surface has no session gate of its own: an account-wide
  // payment mints no per-case session, so `full` is always on offer here.
  // Held until both loads settle, because until then the gates read config and balance defaults.
  useEffect(() => {
    if (configLoading || isLoading || !tabs) return;
    const next = resolveActivePaymentTab(paymentTab, tabs);
    if (next !== paymentTab) setPaymentTab(next);
  }, [paymentTab, tabs, configLoading, isLoading]);

  // Direct navigation to an account with no account surface falls back to the dashboard.
  useEffect(() => {
    if (!accountId && !isPreviewMode) {
      navigate('/login');
      return;
    }

    if (!isLoading && offer.surface === 'none') {
      navigate(dashboardPath);
    }
  }, [accountId, isLoading, offer.surface, navigate, isPreviewMode, dashboardPath]);

  // Must stay below the hooks above — returning ahead of them changes hook order between renders.
  if (configLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  // Render nothing while the useEffect redirect is in flight for 0/1-debt
  // accounts or mixed-currency accounts; the "no live debts" empty state would
  // otherwise render with copy that's only accurate for 0 debts.
  if (offer.surface === 'none') {
    return null;
  }

  // Both scopes render the same card; only the destination differs. A case-level plan sends
  // them to the debt list rather than one case: several cases may each have one, and each is
  // cancelled on its own surface.
  let block: { variant: 'plan' | 'case'; to: string } | null = null;
  if (blockedBy === 'account_plan') {
    block = { variant: 'plan', to: accountPaymentPath('plan') };
  } else if (blockedBy === 'case_plan') {
    block = { variant: 'case', to: dashboardPath };
  }

  // Combined account view can include offers across multiple cases — show the
  // headline (highest) pay_now and payment_plan percentage so the customer sees
  // immediately that a discount is on the table without per-case math.
  let topPayNowPct = 0;
  let topPlanPct = 0;
  for (const d of debts) {
    const off = activeOffers[d.id];
    if (!off || off.status === 'expired' || off.status === 'superseded') continue;
    const payNow = off.configSnapshot.options?.pay_now;
    const plan = off.configSnapshot.options?.payment_plan;
    if (payNow?.enabled && payNow.percentage > topPayNowPct) topPayNowPct = payNow.percentage;
    if (plan?.enabled && plan.percentage > topPlanPct) topPlanPct = plan.percentage;
  }
  const planDiscountLabel = topPlanPct > 0 ? t('discount.upToPercentOff', { pct: Math.round(topPlanPct * 100) }) : null;

  return (
    <Layout showFooter={false}>
      <div className="mx-auto flex-1 stack w-full min-h-0">
        <Tabs variant="default" value={mainTab} onValueChange={(v) => setMainTab(v as 'payment' | 'breakdown')} className="space-y-2 flex-1 min-h-0">
          <DebtPageHeader backTo={isPreviewMode ? '/dashboard?preview=true' : '/dashboard'} creditor={creditor}>
            <TabsList className="max-w-lg w-full mx-auto max-sm:h-auto">
              <TabsTrigger value="payment" className="rounded-full max-xs:text-sm">
                <CreditCard className="size-4 max-xs:hidden text-muted-foreground" />
                {t('payment.payment')}
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="rounded-full max-xs:text-sm">
                <Landmark className="size-4 max-xs:hidden text-muted-foreground" />
                {t('payment.details')}
              </TabsTrigger>
            </TabsList>
          </DebtPageHeader>

          <TabsContent value="payment" className="space-y-4 w-full mx-auto flex flex-col min-h-0">
            {blockedBy === 'scheduled_account_payment' ? (
              <>
                <ScheduledPaymentPanel
                  amount={scheduledAmount}
                  currency={debts[0]?.currency}
                  scheduledDate={scheduledDate}
                  paymentMethod={scheduledMethod}
                  onCancel={handleCancelScheduledPayment}
                  isCancelling={isCancellingScheduled}
                  error={scheduledError}
                />
                <p className="xs:max-w-lg mx-auto w-full text-center text-sm text-muted-foreground">
                  {t('payment.scheduledPanel.accountUnavailableNote')}
                </p>
              </>
            ) : block ? (
              // Cancelling lives at the destination, never in place.
              <AccountPaymentBlock variant={block.variant} onView={() => navigate(block.to)} />
            ) : paymentTab === 'plan' && tabs?.plan ? (
              <PaymentOptionView title={t('payment.paymentPlan')} onClose={() => setPaymentTab('full')}>
                <AccountPaymentPlanTab
                  accountId={accountId}
                  debts={debts}
                  totalAmount={totalBalance}
                  paymentPlanConfig={config.payment.paymentPlan}
                  onReview={handlePlanReview}
                  returnNotice={planReturnNotice}
                  initialFrequency={reviewPlanData ? reviewPlanData.frequency : linkPrefill.frequency}
                  initialStartDate={reviewPlanData ? toScheduleDate(reviewPlanData.startDate) : linkPrefill.start}
                  initialPaymentCount={reviewPlanData ? String(reviewPlanData.paymentCount) : linkPrefill.count}
                  initialMethod={reviewPlanData?.paymentMethod}
                  decidedPlan={decidedPlan}
                  onOfferSubmitted={() => setDecidedPlan(null)}
                  assessmentReturned={linkPrefill.offer === 'assessed'}
                />
                {reviewPlanData && (
                  <PlanAuthoriseSheet
                    open={showPlanReview}
                    onClose={handleEditPlan}
                    totalAmount={reviewPlanData.totalAmount}
                    currency={debts[0]?.currency}
                    clientName={reviewClientName}
                    plan={{ startDate: reviewPlanData.startDate, frequency: reviewPlanData.frequency, paymentCount: reviewPlanData.paymentCount }}
                    onAuthorise={executePlanFromReview}
                    isProcessing={isProcessingReview}
                    error={reviewError}
                  />
                )}
              </PaymentOptionView>
            ) : paymentTab === 'partial' && tabs?.partial ? (
              <PaymentOptionView title={t('payment.partialPayment')} onClose={() => setPaymentTab('full')}>
                <AccountPartialPaymentTab
                  accountId={accountId}
                  debts={debts}
                  totalBalance={payableBalance}
                  allowDateSelect={config.payment.allowPartialPayLater}
                  minPartialPaymentAmount={config.payment.minPartialPaymentAmount}
                />
              </PaymentOptionView>
            ) : (
              <AccountPayInFullTab
                accountId={accountId}
                debts={debts}
                amount={totalBalance}
                allowFullPayLater={config.payment.allowFullPayLater}
                onChoosePlan={tabs?.plan ? () => setPaymentTab('plan') : undefined}
                onChoosePartial={tabs?.partial ? () => setPaymentTab('partial') : undefined}
                planDiscountLabel={planDiscountLabel}
                optionsPlacement={config.payment.paymentOptionsPlacement}
              />
            )}
          </TabsContent>

          <TabsContent value="breakdown" className="stack flex-1 h-full">
            <AccountDebtBreakdown debts={debts} totalBalance={payableBalance} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
